// ./src/utils/api/EventZoneService.ts
//
// Event Zone (area polygons drawn on the event map — perimeter, parking, camping…)
// service. OpResult-native, event-scoped. Full single-item CRUD plus a bulk
// status/category change / remove (array of ids in the where clause). Field names
// mirror the eventsmagic-api EventZone SDL.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

export interface MapPoint {
  lat: number;
  lng: number;
}

const ZONE_FIELDS = `
  id
  eventId
  accountId
  categoryId
  name
  color
  points { lat lng }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query EventZoneList($filter: EventZoneFilter, $params: PaginationAndSorting) {
    eventZoneList(filter: $filter, params: $params) {
      code
      data { ${ZONE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query EventZoneGet($id: ID) {
    eventZoneGet(id: $id) {
      code
      data { ${ZONE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventZoneCreate($params: EventZoneInput) {
    eventZoneCreate(params: $params) {
      code
      data { ${ZONE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventZoneUpdate($where: EventZoneWhereInput, $params: EventZoneInput) {
    eventZoneUpdate(where: $where, params: $params) {
      code
      data { ${ZONE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventZoneRemove($where: EventZoneWhereInput) {
    eventZoneRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventZone schema)
// =============================================================================

export interface EventZone {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  color?: string | null;
  points?: MapPoint[] | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventZoneInput {
  eventId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  color?: string | null;
  points?: MapPoint[] | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status/category (server-enforced).
export interface EventZoneBulkInput {
  status?: number | null;
  categoryId?: string | null;
}

export interface EventZoneFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface EventZoneWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class EventZoneService {
  static async list(filter?: EventZoneFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async create(params: EventZoneInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(where: EventZoneWhereInput, params: EventZoneInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status/category on every selected zone in one call. */
  static async bulkUpdate(ids: string[], params: EventZoneBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected zone in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id: ids } });
  }
}
